import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiError } from "../../api/client";
import { warehouseApi, type StockRefresh, type StockRefreshStep } from "../../api/warehouse";
import Ic from "../../components/icons.jsx";
import { formatNumber } from "../../lib/format";

/** Koliko često se prati čitanje koje traje. */
const KORAK_MS = 1000;

/**
 * „Osveži stanje“: stanje se čita iz ERP-a odmah, a ne za pet minuta, i čitanje se prati korak po korak (veza sa
 * ERP-om, čitanje, upis u portal) do kraja: uspeh, ili neuspeh sa razlogom. Dugme stoji u zaglavlju strane, a panel
 * koraka ispod njega, u izgledu koraka uvoza porudžbina. Čitanje koje je pokrenuo neko drugi, ili koje je trajalo kad
 * je strana otvorena, prati se isto.
 */
export function useOsveziStanje(onOsvezeno: () => void) {
  const { t } = useTranslation();
  const [citanje, setCitanje] = useState<StockRefresh | null>(null);
  const [vidi, setVidi] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const [sada, setSada] = useState(() => Date.now());
  const posle = useRef(onOsvezeno);
  posle.current = onOsvezeno;

  const radi = citanje?.status === "RUNNING";

  useEffect(() => {
    let vazi = true;
    warehouseApi.stockRefresh()
      .then((c) => {
        if (vazi && c?.status === "RUNNING") {
          setCitanje(c);
          setVidi(true);
        }
      })
      .catch(() => undefined);
    return () => { vazi = false; };
  }, []);

  // Dok čitanje traje, prati se svake sekunde; kraj sa uspehom ponovo čita spisak robe.
  useEffect(() => {
    if (!radi || !citanje) return;
    const id = citanje.id;
    const tajmer = window.setTimeout(() => {
      warehouseApi.stockRefresh()
        .then((c) => {
          if (!c || c.id !== id) return;
          setCitanje(c);
          if (c.status === "SUCCESS") posle.current();
        })
        // Prolazna greška mreže: pokušava se ponovo za sekund.
        .catch(() => setCitanje((s) => (s ? { ...s } : s)));
    }, KORAK_MS);
    return () => window.clearTimeout(tajmer);
  }, [radi, citanje]);

  useEffect(() => {
    if (!radi) return;
    const sat = window.setInterval(() => setSada(Date.now()), 1000);
    return () => window.clearInterval(sat);
  }, [radi]);

  async function pokreni() {
    setGreska(null);
    setVidi(true);
    setSada(Date.now());
    try {
      setCitanje(await warehouseApi.refreshStock());
    } catch (e) {
      setCitanje(null);
      setGreska((e as ApiError).message);
    }
  }

  const naziv = (s: StockRefreshStep) => ({
    CONNECT: t("warehouse.refresh.connect", "Veza sa ERP bazom"),
    READ: t("warehouse.refresh.read", "Čitanje stanja iz ERP-a"),
    SAVE: t("warehouse.refresh.save", "Upis stanja u portal"),
  }[s.step]);

  const opis = (s: StockRefreshStep) => {
    if (s.step === "READ" && s.count != null && s.state !== "WAITING") {
      return t("warehouse.refresh.rowsRead", "pročitano redova: {{n}}", { n: formatNumber(s.count) });
    }
    if (s.step === "SAVE" && s.state === "DONE" && s.count != null) {
      return t("warehouse.refresh.written", "upisano pozicija: {{n}}", { n: formatNumber(s.count) });
    }
    return null;
  };

  const sekundi = citanje ? Math.max(0, Math.round((sada - new Date(citanje.startedAt).getTime()) / 1000)) : 0;

  const dugme = (
    <button type="button" className="btn" onClick={() => void pokreni()} disabled={radi}>
      <Ic.refresh /> {radi ? t("warehouse.refresh.running", "Osvežava se…") : t("warehouse.refresh.button", "Osveži stanje")}
    </button>
  );

  const panel = vidi && (citanje || greska) ? (
    <div className="card oi-panel" style={okvir} role="status" aria-live="polite">
      <div style={glava}>
        <h2 style={{ margin: 0, fontSize: 15, color: "var(--ink)" }}>
          {t("warehouse.refresh.title", "Osvežavanje stanja iz ERP-a")}
        </h2>
        {!radi && (
          <button type="button" className="btn sm" aria-label={t("warehouse.refresh.close", "Zatvori")}
                  onClick={() => setVidi(false)}>
            <Ic.x />
          </button>
        )}
      </div>
      {citanje?.steps.map((s, i) => {
        const izgled = { WAITING: "pending", RUNNING: "active", DONE: "done", FAILED: "failed" }[s.state];
        const detalj = opis(s);
        return (
          <div key={s.step} className={`oi-stage-row ${izgled}`} data-korak={s.step} data-stanje={s.state}>
            <span className="oi-stage-ic">
              {s.state === "DONE" ? <Ic.check /> : s.state === "FAILED" ? <Ic.x />
                : <span style={{ fontSize: 10.5, fontWeight: 700 }}>{i + 1}</span>}
            </span>
            <div style={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <span className="oi-stage-label">{naziv(s)}</span>
              {detalj && <span style={tiho}>{detalj}</span>}
              {s.state === "RUNNING" && <span style={tiho}>{t("warehouse.refresh.seconds", "{{n}} s", { n: sekundi })}</span>}
            </div>
          </div>
        );
      })}
      {citanje?.status === "SUCCESS" && (
        <div style={kraj}>
          <span className="tag ok">
            {t("warehouse.refresh.success", "Stanje je osveženo. Pozicija na stanju: {{n}}.", {
              n: formatNumber(citanje.positions ?? 0),
            })}
            {(citanje.newLots ?? 0) > 0
              && ` ${t("warehouse.refresh.newLots", "Novih LOT-ova: {{n}}.", { n: formatNumber(citanje.newLots ?? 0) })}`}
          </span>
        </div>
      )}
      {(citanje?.status === "FAILED" || greska) && (
        <div style={{ ...kraj, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
          <span className="tag err">
            {t("warehouse.refresh.failed", "Osvežavanje nije uspelo. Prikazano je poslednje pročitano stanje.")}
          </span>
          <span style={{ ...tiho, overflowWrap: "anywhere" }}>{citanje?.failure ?? greska}</span>
          <button type="button" className="btn sm" onClick={() => void pokreni()}>
            {t("warehouse.refresh.retry", "Pokušaj ponovo")}
          </button>
        </div>
      )}
    </div>
  ) : null;

  return { dugme, panel };
}

const okvir: React.CSSProperties = { maxWidth: 620, margin: 0, padding: "16px 20px", width: "100%" };
const glava: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6 };
const tiho: React.CSSProperties = { fontSize: 12.5, color: "var(--ink-3)" };
const kraj: React.CSSProperties = { marginTop: 10 };
